import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import StatusBadge from "@/components/common/StatusBadge";
import api from "@/lib/apiClient";
import { Link } from "react-router-dom";
import {
  Truck, Scales, Package, Drop, Factory, Warning, ArrowUpRight, Buildings, ShieldCheck,
} from "@phosphor-icons/react";

const STAT_CARDS = [
  { key: "pending_approval", label: "Menunggu Approve", icon: ShieldCheck, tone: "red", to: "/approval" },
  { key: "receiving_today", label: "Receiving Hari Ini", icon: Package, tone: "sky", to: "/receiving" },
  { key: "delivery_today",  label: "Delivery Hari Ini",  icon: Buildings, tone: "sky", to: "/delivery" },
  { key: "vehicles_in_area", label: "Kendaraan di Area", icon: Truck, tone: "navy", to: "/log-kendaraan" },
  { key: "active_receiving", label: "Receiving Aktif",   icon: Package, tone: "amber", to: "/receiving" },
  { key: "active_loading",  label: "Loading Aktif",      icon: Factory, tone: "amber", to: "/loading" },
  { key: "opname_today",    label: "Opname Selesai",     icon: Drop, tone: "emerald", to: "/opname" },
  { key: "critical_variance", label: "Variance Kritis",  icon: Warning, tone: "red", to: "/opname" },
  { key: "tanks_attention", label: "Tangki Perlu Perhatian", icon: Drop, tone: "red", to: "/tanks" },
];

const TONE = {
  navy:    "bg-slate-900 text-white",
  sky:     "bg-sky-50 text-sky-900 border border-sky-100",
  amber:   "bg-amber-50 text-amber-900 border border-amber-100",
  emerald: "bg-emerald-50 text-emerald-900 border border-emerald-100",
  red:     "bg-red-50 text-red-900 border border-red-100",
};

export default function DashboardPage() {
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState({});
  const [tankAlerts, setTankAlerts] = useState({ items: [], count: 0, critical_count: 0 });

  const loadAll = () => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(()=>{});
    api.get("/dashboard/recent").then((r) => setRecent(r.data)).catch(()=>{});
    api.get("/dashboard/tank-alerts").then((r) => setTankAlerts(r.data)).catch(()=>{});
  };
  useAutoRefresh(loadAll, { interval: 300_000 });

  return (
    <AppShell title="Dashboard Operasional" subtitle="Ringkasan aktivitas pabrik hari ini · realtime">
      {/* Stat Bento grid — only cards the backend deemed visible for this role */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.filter((c) => c.key in stats).map((c, i) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={`stat-${c.key}`}
            className={`group rounded-xl p-5 fade-up ${TONE[c.tone]} shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-transform duration-200 relative overflow-hidden`}
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-70">{c.label}</div>
              <c.icon size={20} weight="duotone" className="opacity-80" />
            </div>
            <div className="font-heading font-black text-4xl mt-3 leading-none">
              {stats[c.key] ?? "—"}
            </div>
            <div className="mt-2 text-[11px] flex items-center gap-1 opacity-70 group-hover:opacity-100">
              Lihat detail <ArrowUpRight size={12} weight="bold" />
            </div>
          </Link>
        ))}
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {tankAlerts.items && tankAlerts.items.length > 0 && (
          <TankAttentionPanel data={tankAlerts} />
        )}
        {recent.gates && (
        <RecentPanel
          title="Gerbang Terakhir"
          testid="recent-gates"
          empty="Belum ada aktivitas gerbang."
          rows={recent.gates.map((g) => ({
            id: g.id,
            title: g.vehicle_no,
            subtitle: `${g.company} · ${g.driver_name}`,
            status: g.status,
            time: g.gate_in_at,
          }))}
        />)}
        {recent.receivings && (
        <RecentPanel
          title="Receiving Terakhir"
          testid="recent-receiving"
          empty="Belum ada receiving."
          rows={recent.receivings.map((r) => ({
            id: r.id,
            title: r.ref_no,
            subtitle: `${r.supplier} · ${r.material}`,
            status: r.status,
            time: r.created_at,
          }))}
        />)}
        {recent.deliveries && (
        <RecentPanel
          title="Delivery Terakhir"
          testid="recent-delivery"
          empty="Belum ada delivery."
          rows={recent.deliveries.map((d) => ({
            id: d.id,
            title: d.do_no,
            subtitle: `${d.customer} · ${d.product}`,
            status: d.status,
            time: d.created_at,
          }))}
        />)}
      </div>
    </AppShell>
  );
}

function TankAttentionPanel({ data }) {
  const fmt = (n) => (n ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
  return (
    <div
      className="rounded-xl bg-white border border-red-200 shadow-sm overflow-hidden lg:col-span-1"
      data-testid="panel-tank-attention"
    >
      <div className="px-5 py-4 border-b border-red-100 flex items-center justify-between bg-gradient-to-r from-red-50 to-white">
        <div className="min-w-0">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-red-800 flex items-center gap-2">
            <Warning size={16} weight="fill" className="text-red-600" />
            Tangki Perlu Perhatian
          </h3>
          <p className="text-[10px] text-red-500 mt-0.5">
            {data.critical_count > 0 && (
              <span className="font-semibold">{data.critical_count} kritis · </span>
            )}
            {data.count} tangki ≥ {data.warn_pct}% kapasitas
          </p>
        </div>
        <Link
          to="/tanks"
          className="text-[11px] text-red-700 font-semibold hover:underline shrink-0"
          data-testid="link-tank-attention-all"
        >
          Lihat →
        </Link>
      </div>
      <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
        {data.items.slice(0, 8).map((t) => {
          const isCrit = t.level === "critical";
          const barColor = isCrit ? "bg-red-500" : "bg-amber-500";
          return (
            <Link
              key={t.tank_id}
              to="/tanks"
              className="block px-5 py-3 hover:bg-slate-50/70 transition-colors"
              data-testid={`tank-alert-${t.code}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">
                    {t.name}
                    <span className="ml-2 text-[10px] font-mono text-slate-400">{t.code}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{t.product}</div>
                </div>
                <div
                  className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                    isCrit ? "bg-red-100 text-red-700 border border-red-200" : "bg-amber-100 text-amber-700 border border-amber-200"
                  }`}
                >
                  {t.percent}%
                </div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className={`${barColor} h-full transition-all`}
                  style={{ width: `${Math.min(t.percent, 100)}%` }}
                />
              </div>
              <div className="mt-1 text-[10px] font-mono text-slate-400">
                {fmt(t.current_stock)} / {fmt(t.capacity)} L
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function RecentPanel({ title, rows, empty, testid }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">{title}</h3>
        <Scales size={16} className="text-slate-400" />
      </div>
      <div className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-slate-400">{empty}</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50/70 transition-colors">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{r.title}</div>
                <div className="text-[11px] text-slate-500 truncate">{r.subtitle}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusBadge status={r.status} />
                <div className="text-[10px] font-mono text-slate-400">
                  {r.time ? new Date(r.time).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-"}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
